import React from 'react'

import { HeadFC, Link, PageProps, graphql } from 'gatsby'

import BlogCardGrid from '@components/Content/BlogCard/BlogCardGrid'
import Hero from '@components/Content/Hero/Hero'
import Wysiwyg from '@components/Content/Wysiwyg/Wysiwyg'
import Layout from '@components/Layout/Layout'
import Section from '@components/Layout/Section'
import NewsSidebar from '@components/News/NewsSidebar'
import NewsTableOfContents from '@components/News/NewsTableOfContents'
import SEO from '@components/SEO/SEO'
import Breadcrumbs from '@components/Woocommerce/Breadcrumbs'

import { BreadcrumbsType } from '@contracts/components/breadcrumbs'
import { NewsSingleDataType } from '@contracts/pages/newsSingle'

import * as styles from './NewsSingle.module.scss'

import BackIcon from '@icons/back.svg'

const newsSingle: React.FunctionComponent<PageProps<NewsSingleDataType>> = (props) => {
	const heroImageRef = React.useRef<HTMLDivElement>(null)

	const page = props.data.wpPost
	const relatedPosts = props.data.relatedPosts.nodes

	page.postOptions.hero.image = page.featuredImage.node

	const breadcrumbsData: BreadcrumbsType = [
		{
			label: 'Home',
			link: '/',
		},
		{
			label: 'News',
			link: '/insights/',
		},
	]

	return (
		<Layout background={'dark'}>
			<Section type="wide" smallPadding hasNoBottomPadding className={styles.breadcrumbs}>
				<Breadcrumbs data={breadcrumbsData} theme="dark" />
			</Section>

			<Section className={styles.section + ' ' + styles.backSection} hasNoPadding={true}>
				<Link to={'/insights/'} className={styles.back + ' ' + styles.heroBack}>
					<BackIcon /> Back
				</Link>
			</Section>

			<Hero
				data={page.postOptions.hero}
				smallTitle={true}
				isNews={true}
				backLink={'/insights/'}
				heroImageRef={heroImageRef}
			/>

			<Section
				className={styles.section + ' ' + styles.quoteSection}
				containerClassName={styles.excerptContainer}
				hasNoPadding={true}
			>
				{page.postOptions.excerpt && <Wysiwyg data={page.postOptions.excerpt} className={styles.excerpt} />}
			</Section>

			<Section
				className={styles.section + ' ' + styles.quoteSection}
				containerClassName={styles.quoteContainer}
				hasNoTopPadding={true}
			>
				<div className={styles.left}>
					{page.content && <NewsTableOfContents data={page.content} />}
					{page.content && (
						<Wysiwyg data={page.content} className={styles.content} isPageContent headingIds />
					)}

					<Link to={'/insights/'} className={styles.back}>
						<BackIcon /> Back
					</Link>
				</div>
				<div className={styles.right}>
					<NewsSidebar data={page.postOptions.sidebar} heroImageRef={heroImageRef} />

					{page.postOptions.quote.content && (
						<Wysiwyg data={page.postOptions.quote.content} className={styles.quote} />
					)}
				</div>
			</Section>

			<Section className={styles.section + ' ' + styles.backSection} hasNoPadding={true}>
				<Link to={'/insights/'} className={styles.back + ' ' + styles.heroBack + ' ' + styles.bottomBack}>
					<BackIcon /> Back
				</Link>
			</Section>

			{relatedPosts.length > 0 && (
				<Section className={styles.relatedPosts}>
					<h2 className={styles.title}>Related Posts</h2>

					<BlogCardGrid
						data={relatedPosts}
						isNews={true}
						hasNextPage={false}
						nextPageLink={false}
						clean={true}
					/>
				</Section>
			)}
		</Layout>
	)
}

export const query = graphql`
	query PostData($id: String!, $categoryId: Int!) {
		wpPost(id: { eq: $id }) {
			title
			uri
			slug
			content
			excerpt
			featuredImage {
				node {
					...wpMediaItem
					localFile {
						childImageSharp {
							gatsbyImageData(width: 1920)
						}
					}
				}
			}
			postOptions {
				hero {
					title
					content
				}
				quote {
					content
				}
				sidebar {
					ctaOrNewsletter
					title
					content
					button {
						buttonType
						label
						internalLink {
							... on WpPage {
								uri
							}
							... on WpPost {
								uri
							}
							... on WpSimpleProduct {
								uri
							}
							... on WpVariableProduct {
								uri
							}
						}
						externalLink
					}
				}
			}
			...postSeo
		}
		relatedPosts: allWpPost(
			sort: { date: DESC }
			limit: 3
			filter: { categories: { nodes: { elemMatch: { databaseId: { eq: $categoryId } } } }, id: { ne: $id } }
		) {
			nodes {
				title
				uri
				slug
				id
				featuredImage {
					node {
						...wpMediaItem
						localFile {
							childImageSharp {
								gatsbyImageData(width: 560)
							}
						}
					}
				}
			}
		}
	}
`

export default newsSingle

export const Head: HeadFC<NewsSingleDataType> = (props) => {
	return (
		<SEO
			data={props.data.wpPost.seo}
			canonical={process.env.GATSBY_CURRENT_DOMAIN + '/insights/' + props.data.wpPost.slug + '/'}
		/>
	)
}
